import { getCandidateRole } from "../../components/utils/candidateRole";

// Field-to-display mapping for the candidate quick-view modal (see
// specs/candidate-quick-view.md). Pure functions over a GET /candidates/ row,
// so they can be tested without rendering the search page.

export const MISSING = "—";

export const getCountryLabel = (country) => {
  if (!country) return "";
  if (typeof country === "object") return country.label || "";
  try {
    const parsed = JSON.parse(country);
    return parsed?.label || country;
  } catch {
    return country;
  }
};

export const CONTRACTUAL_STATUS_LABELS = {
  candidate: "Candidate",
  intern: "Intern",
  subconsultant: "Subconsultant",
  employee: "Employed",
  "alumni/inactive": "Alumni",
};

// User.roles is the Keycloak realm role list, which also carries Keycloak's
// own defaults (offline_access, uma_authorization, default-roles-*). Only the
// app roles are shown, highest first.
const SYSTEM_ROLE_LABELS = [
  ["admin", "Admin"],
  ["hr", "HR"],
  ["subconsultant", "Subconsultant"],
  ["candidate", "Candidate"],
];

const text = (value) => {
  if (value == null) return MISSING;
  const s = String(value).trim();
  return s || MISSING;
};

const list = (values) =>
  (Array.isArray(values) ? values : []).map((v) => (v == null ? "" : String(v).trim())).filter(Boolean);

export const formatSystemRole = (roles) => {
  const owned = list(roles).map((r) => r.toLowerCase());
  const match = SYSTEM_ROLE_LABELS.find(([role]) => owned.includes(role));
  return match ? match[1] : MISSING;
};

export const formatContractualStatus = (status) => text(CONTRACTUAL_STATUS_LABELS[status] || status);

export const formatAccountStatus = (active) =>
  active === true ? "Active" : active === false ? "Inactive" : MISSING;

export const formatNotice = (noticeTime, noticeTimeOther) => {
  if (noticeTime === "1 Month") return "1 month";
  if (noticeTime === "2 Weeks") return "2 weeks";
  if (noticeTime === "Other") return text(noticeTimeOther) === MISSING ? "Other" : text(noticeTimeOther);
  return text(noticeTime);
};

// Mockup style: largest whole unit, title case, no "ago" ("3 Days",
// "2 Weeks").
export const formatRelativeTime = (date, now = new Date()) => {
  if (!date) return MISSING;
  const d = new Date(date);
  if (isNaN(d.getTime())) return MISSING;
  const diffMs = Math.max(0, now - d);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const amount = (n, unit) => `${n} ${unit}${n !== 1 ? "s" : ""}`;
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return amount(diffMins, "Minute");
  if (diffHours < 24) return amount(diffHours, "Hour");
  if (diffDays < 7) return amount(diffDays, "Day");
  if (diffDays < 30) return amount(Math.floor(diffDays / 7), "Week");
  if (diffDays < 365) return amount(Math.floor(diffDays / 30), "Month");
  return amount(Math.floor(diffDays / 365), "Year");
};

// Published Status: null when the candidate document has no value, so the
// modal shows a dash rather than a pill.
export const formatPublished = (includeInTalentPool) =>
  includeInTalentPool === true
    ? { label: "Published", published: true }
    : includeInTalentPool === false
      ? { label: "Unpublished", published: false }
      : null;

export const toQuickViewFields = (candidate, now = new Date()) => {
  const c = candidate || {};
  const u = c.user || {};
  const name = [u.firstName, u.lastName].map((n) => (n || "").trim()).filter(Boolean).join(" ");
  const location = [(u.city || "").trim(), getCountryLabel(u.country).trim()].filter(Boolean).join(", ");
  const engagement = list(c.engagementTypes);
  return {
    name: name || MISSING,
    primaryRole: text(getCandidateRole(c)),
    location: location || MISSING,
    systemRole: formatSystemRole(u.roles),
    contractualStatus: formatContractualStatus(c.contractualStatus),
    accountStatus: formatAccountStatus(u.active),
    seniority: text(c.seniority),
    secondaryRoles: list(c.secondaryRoles),
    engagementPreferences: engagement.length > 0 ? engagement.join(", ") : MISSING,
    sideAvailability: text(c.sideAssignments),
    noticePeriod: formatNotice(c.noticeTime, c.noticeTimeOther),
    published: formatPublished(c.includeInTalentPool),
    lastSignIn: formatRelativeTime(u.lastSignIn, now),
    previousContractualStatus: formatContractualStatus(c.previousContractualStatus),
  };
};
